<template>
  <div>
    <preloader v-if="isLoading" />
    <template v-else>
      <component :is="layout">
        <router-view />
      </component>

      <transition name="fade">
        <law-restriction-popup v-if="canShowLawRestrictionPopup" />
      </transition>

      <template v-if="layoutName !== LAYOUT_BLANK">
        <transition name="fade">
          <cookie-consent v-if="canShowCookieConsent" />
          <cookies-popup v-else-if="canShowCookiesPopup" />
        </transition>
      </template>
    </template>

    <transition name="fade">
      <theater-player v-if="isTheaterMode" />
    </transition>
  </div>
</template>

<script setup lang="ts">
import { ApiError } from "@/api/ApiError";
import Preloader from "@/components/preloader/index.vue";
import { changeLanguage } from "@/i18n";
import { Visit } from "@/interfaces/Visit";
import getFlash from "@/utils/getFlash";
import googleSignup from "@/utils/googleSignup";
import { getApprovedCookies, loadLanguage, removeAccessToken } from "@/utils/storage";
import { getTld } from "@/utils/urlHelper";
import { LAYOUT_BLANK, LAYOUT_LADIES, LAYOUT_LADIES_V2, LAYOUT_V2, LAYOUT_V2_BLOG, ROUTE_SCROLL_DELAY } from "@/utils/variables";
import Bugsnag from "@bugsnag/js";
import { computed, defineAsyncComponent, nextTick, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import { useCompanyStore } from "@/store/company";
import { useFeatureStore } from "@/store/feature";
import { useVisitStore } from "@/store/visit";
import { useUserStore } from "@/store/user";
import { usePopupStore } from "@/store/popup";
import { useLayoutStore } from "@/store/layout";
import { usePlayerStore } from "@/store/player";
import browserHelper from "@/utils/browserHelper.ts";
import { useRoleStore } from "@/store/role.ts";
import { initClarity } from "@/utils/clarity";

const CookieConsent = defineAsyncComponent(() => import("@/components/v2/cookie-consent/index.vue"));
const CookiesPopup = defineAsyncComponent(() => import("@/components/cookies-popup/index.vue"));
const layout_main = defineAsyncComponent(() => import("./components/layout-main/index.vue"));
const layout_blank = defineAsyncComponent(() => import("@/components/layout-blank/index.vue"));
const layout_ladies = defineAsyncComponent(() => import("@/components/layout-ladies/index.vue"));
const layout_ladies_v2 = defineAsyncComponent(() => import("@/components/v2/layout-ladies-v2/index.vue"));
const layout_v2 = defineAsyncComponent(() => import("@/components/v2/layout/index.vue"));
const layout_v2_blog = defineAsyncComponent(() => import("@/components/v2/layout-blog/index.vue"));
const TheaterPlayer = defineAsyncComponent(() => import("@/components/theater-player/index.vue"));
const LawRestrictionPopup = defineAsyncComponent(() => import("@/components/popups/law-restriction-popup/index.vue"));

defineOptions({
  name: "app",
});
const companyStore = useCompanyStore();
const featureStore = useFeatureStore();
const visitStore = useVisitStore();
const userStore = useUserStore();
const popupStore = usePopupStore();
const layoutStore = useLayoutStore();
const playerStore = usePlayerStore();
const roleStore = useRoleStore();

const route = useRoute();
const { locale } = useI18n();

const canShowCookieConsent = ref(false);

const isLoggedIn = computed(() => userStore.isLoggedIn);
const profile = computed(() => userStore.getProfile);
const canShowCookiesPopup = computed(() => popupStore.canShowCookiesPopup);
const canShowLawRestrictionPopup = computed(() => popupStore.canShowLawRestrictionPopup);
const isLoading = computed(() => layoutStore.getGlobalLoading);
const isBackNavigation = computed(() => layoutStore.isBackNavigation);
const isContentLoaded = computed(() => layoutStore.getContentLoaded);
const pagePosition = computed(() => layoutStore.getPagePosition);
const visit = computed(() => visitStore.getVisit);
const isTheaterMode = computed(() => playerStore.isTheaterMode);
const hasFrench = computed(() => featureStore.hasFrench);

const layoutName = computed(() => {
  return typeof route.meta.layout === "function" ? route.meta.layout() : route.meta.layout;
});

const layout = computed(() => {
  switch (layoutName.value) {
    case LAYOUT_BLANK:
      return layout_blank;
    case LAYOUT_LADIES:
      return layout_ladies;
    case LAYOUT_LADIES_V2:
      return layout_ladies_v2;
    case LAYOUT_V2:
      return layout_v2;
    case LAYOUT_V2_BLOG:
      return layout_v2_blog;
    default:
      return layout_main;
  }
});

const bodyLayoutClass = computed(() => {
  switch (layout.value) {
    case layout_v2:
    case layout_v2_blog:
    case layout_ladies_v2:
      return "v2_freearea";
    case layout_ladies:
      return "layout-ladies";
    default:
      return "layout-memberarea";
  }
});

const scrollOnBackPossible = computed(() => {
  return isBackNavigation.value && isContentLoaded.value;
});

const checkCookieConsent = () => {
  canShowCookieConsent.value = getTld() !== "io" && visitStore.isEUCountry && !getApprovedCookies();
};

watch(route, () => {
  // todo: fix - window.history.state.position is NaN
  layoutStore.setBackNavigation(window.history.state?.position + 2 <= window.history.length);
  checkCookieConsent();
  googleSignup.togglePrompt();
});

watch(locale, (newVal, oldVal) => {
  if (newVal !== oldVal && route.meta.title) {
    document.title = typeof route.meta.title === "function" ? route.meta.title() : route.meta.title;
  }
});

watch(isLoggedIn, (value) => {
  if (!value) {
    if (isTheaterMode.value) {
      playerStore.setTheaterMode({ videoId: null });
    }

    googleSignup.init();
  }

  companyStore.defineCompany(false);
  roleStore.load();
});

watch(visit, (value: Visit | null, oldValue: Visit | null) => {
  if (!value || oldValue) {
    return;
  }

  companyStore.defineCompany(false);
  roleStore.load();

  checkCookieConsent();
  initClarity();

  if (value.new || getFlash("first_visit")) {
    const lang = hasFrench.value && value.country === "FR" ? "fr" : value.is_international ? "en" : "de";

    if (locale.value !== lang) {
      changeLanguage(lang);
    }
  }
});

watch(bodyLayoutClass, (newVal, oldVal) => {
  if (newVal) {
    document.body.classList.add(newVal);
  }

  if (oldVal) {
    document.body.classList.remove(oldVal);
  }
});

watch(scrollOnBackPossible, (value) => {
  //todo: fix - scrollOnBackPossible is always false
  if (value && pagePosition.value !== null) {
    nextTick(() => {
      setTimeout(() => {
        window.scroll({
          top: pagePosition.value?.top,
          behavior: "instant",
        });
      }, ROUTE_SCROLL_DELAY + 10);
    });
  }
});

changeLanguage(loadLanguage() || document.documentElement.getAttribute("lang") || "en");
layoutStore.setGlobalLoading(true);
document.body.classList.add(bodyLayoutClass.value);

googleSignup.init();
browserHelper.init();

window.addEventListener("unhandledrejection", (e) => {
  e.preventDefault();
  const status = e.reason?.status?.status;

  if (status !== 401) {
    if (e.reason instanceof ApiError && Bugsnag.isStarted()) {
      Bugsnag.notify(e.reason);
    } else {
      console.debug(e);
    }
  } else {
    if (profile.value) {
      removeAccessToken();
      userStore.logout({
        path: "/#log_in",
        query: { redirect: route.fullPath },
      });
    }
  }
});
</script>
